/**
 * WhatsAppNotificationPanel
 *
 * Floating bottom-right panel that surfaces inbound WhatsApp messages
 * for leads relevant to the current user:
 *
 *  - Regular users  → messages from their own assigned leads only
 *  - sales-manager  → messages from their own assigned leads
 *                     + messages from leads with no assignee (assignedTo == null/"")
 *
 * Visibility: chip only appears when totalUnread > 0.
 * Auto-hides: when ChannelChat marks messages as read (sets readAt in Firestore),
 *             the real-time listener recalculates and hides the chip automatically.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Badge,
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  SwipeableDrawer,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {
  Timestamp,
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  increment,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { minutesSince, receptionSummary, takeReceptionVisit, useReceptionToday } from '../utils/reception';
import { useMyPresenterDayAccess } from '../utils/presenterAccess';
import type { ReceptionVisit } from '../utils/reception';
import { dueText, isOverdue, needsActionNow, useMyTodayUrgent, visitPhase } from './myTodayUrgent';
import type { UrgentItem } from './myTodayUrgent';
import app from '../firebase';
import { useAuth, usePermissions } from '../AuthContext';
import {
  normalizeContactId,
  normalizePhoneDigits,
  formatBrokerDisplayName,
  formatPhoneDisplay,
  formatMessageTimestamp,
} from '../utils/messaging';
import type { ChannelMessage, Conversation } from '../types/messaging';
import ChannelChat from './ChannelChat';
import FinishVisitDialog from './FinishVisitDialog';
import IgnoreMenu from './IgnoreMenu';
import { displayName, IGNORE_REASONS, markItemDone, resolveReengagement as resolveLeadReengagement } from '../utils/myTodayActions';
import { useConfirmAction } from './ConfirmActionDialog';
import { OPEN_WHATSAPP_PANEL_EVENT } from '../utils/whatsappPanel';
import type { OpenWhatsAppPanelDetail } from '../utils/whatsappPanel';

// ─── Design tokens ────────────────────────────────────────────────────────────
const WA_GREEN  = '#25D366';
const HEADER_BG = '#1c1b19';
const GOLD      = '#9d6f32';
const RED       = '#c62828';
const DARK      = '#1a1a1a';
const MUTED     = '#6b6b6b';
const BORDER    = '#e8e8e8';
const FONT_BODY = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const PANEL_W   = 391;  // +15% from 340
const CHAT_H    = 437;  // +15% from 380
const LIST_H    = 322;  // +15% from 280

const AVATAR_COLORS = [
  { bg: '#E6F1FB', color: '#185FA5' },
  { bg: '#FAEEDA', color: '#854F0B' },
  { bg: '#EAF3DE', color: '#3B6D11' },
  { bg: '#EEEDFE', color: '#534AB7' },
  { bg: '#FBEAF0', color: '#993556' },
];

function avatarStyle(phone: string) {
  const idx = (phone.charCodeAt(phone.length - 1) || 0) % AVATAR_COLORS.length;
  return AVATAR_COLORS[idx];
}

function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .map((w) => w[0]?.toUpperCase() || '')
    .slice(0, 2)
    .join('');
}

// "Visited" moves the lead to Site Visited only from these earlier stages
// (New, Qualify, Prospect, Appointment, CNR, Reopen).
const SITE_VISITED_STATUS = 'GqoIk5tdthxSlgxG0hm3';
const VISITED_FROM = new Set([
  'T3lm25fKlGfyOCfLKeCK', 'kBo3JLBgGJy24xZpy6ma', 'x2fXZwvKQmbOuYitZOTb',
  'awBRIRssXm0l6WuxOz6B', 'XQWbmRcQkolUccev198i', '1tQ66IOxzV5zh2yGKtmM',
]);

// ─── Internal lead shape ──────────────────────────────────────────────────────
interface MyLead {
  id: string;
  name: string;
  phone10: string;
  projectId?: string | null;
  isUnassigned?: boolean; // true = no owner, shown to sales-manager
}

interface MyBroker {
  id: string;
  name: string;
  agencyName: string | null;
  phone10: string;
}

type CustomerMapEntry = {
  projectId: string;
  projectName: string;
  unitId: string;
  unitNumber: string;
  fullName: string;
};

// ─── Helper: extract phone10 from a Firestore lead doc ───────────────────────
function extractPhone10(data: Record<string, any>): string {
  const raw = String(data.phone10 || data.phone || '').replace(/\D/g, '');
  return raw.length >= 10 ? raw.slice(-10) : raw;
}

function buildCustomerDisplayName(row: Record<string, unknown>): string {
  const direct = String(row.displayName || row.name || '').trim();
  if (direct) return direct;
  return [
    String(row.firstName || '').trim(),
    String(row.middleName || '').trim(),
    String(row.surname || row.lastName || '').trim(),
  ]
    .filter(Boolean)
    .join(' ')
    .trim();
}

function extractCustomerPhoneDigits(row: Record<string, unknown>): string {
  return normalizePhoneDigits(
    String(
      row.primaryCustomerMobile ||
      row.customerMobile ||
      row.mobile ||
      row.mobileNo ||
      row.phone ||
      row.phoneNumber ||
      ''
    ).trim()
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Exported hook — used by AllLeadsPage and FollowUpPage for per-row badges
// Returns Map<phone10, unreadCount> scoped to the current user's relevant leads
// ─────────────────────────────────────────────────────────────────────────────
export function useMyWhatsAppUnread(): Map<string, number> {
  const { currentUser, roleId } = useAuth();
  const db = getFirestore(app);
  const isSalesManager = String(roleId || '') === 'sales-manager';

  const [myPhones, setMyPhones]           = useState<Set<string>>(new Set());
  const [unassignedPhones, setUnassignedPhones] = useState<Set<string>>(new Set());
  const [unreadMap, setUnreadMap]         = useState<Map<string, number>>(new Map());

  // ── Query 1: my assigned leads ──────────────────────────────────────────
  useEffect(() => {
    const email = String(currentUser?.email || '').trim();
    if (!email) { setMyPhones(new Set()); return; }
    const q = query(collection(db, 'leads'), where('assignedTo', '==', email), limit(200));
    const unsub = onSnapshot(q, (snap) => {
      const phones = new Set<string>();
      snap.docs.forEach((d) => {
        const p10 = extractPhone10(d.data());
        if (p10) phones.add(p10);
      });
      setMyPhones(phones);
    }, () => setMyPhones(new Set()));
    return unsub;
  }, [currentUser?.email, db]);

  // ── Query 2: unassigned leads (sales-manager only) ──────────────────────
  useEffect(() => {
    if (!isSalesManager) { setUnassignedPhones(new Set()); return; }
    // Leads where assignedTo field is missing or empty string
    const q = query(collection(db, 'leads'), where('assignedTo', '==', ''), limit(200));
    const unsub = onSnapshot(q, (snap) => {
      const phones = new Set<string>();
      snap.docs.forEach((d) => {
        const p10 = extractPhone10(d.data());
        if (p10) phones.add(p10);
      });
      setUnassignedPhones(phones);
    }, () => setUnassignedPhones(new Set()));
    return unsub;
  }, [isSalesManager, db]);

  // Sales team: unread counts from their own chat cards (they can read a
  // contact's messages one chat at a time, not 30 at once).
  const roleLower = String(roleId || '').toLowerCase();
  const fromCards = !['admin', 'super-admin', 'sales-manager'].includes(roleLower);
  useEffect(() => {
    const email = String(currentUser?.email || '').trim().toLowerCase();
    if (!fromCards || !email) return;
    return onSnapshot(
      query(collection(db, 'whatsappChats'), where('assignedTo', '==', email), orderBy('lastMessageAt', 'desc'), limit(500)),
      (snap) => {
        const map = new Map<string, number>();
        snap.docs.forEach((d) => {
          const n = Number((d.data() as any).unreadCount || 0);
          if (n > 0) map.set(d.id.slice(-10), n);
        });
        setUnreadMap(map);
      },
      () => setUnreadMap(new Map())
    );
  }, [fromCards, currentUser?.email, db]);

  // ── Combined phones → messages listener ─────────────────────────────────
  useEffect(() => {
    if (fromCards) return;
    const allPhones = new Set([...Array.from(myPhones), ...Array.from(unassignedPhones)]);
    const phonesArr = Array.from(allPhones)
      .map((p10) => `91${p10}`)
      .slice(0, 30);

    if (phonesArr.length === 0) { setUnreadMap(new Map()); return; }

    const q = query(
      collection(db, 'whatsappMessages'),
      where('contactPhone', 'in', phonesArr),
      where('direction', '==', 'inbound'),
      orderBy('createdAt', 'desc'),
      limit(500),
    );
    const unsub = onSnapshot(q, (snap) => {
      const map = new Map<string, number>();
      snap.docs.forEach((d) => {
        const data = d.data();
        if (data.readAt) return;
        const raw = String(data.contactPhone || '').replace(/\D/g, '');
        const p10 = raw.length >= 10 ? raw.slice(-10) : raw;
        if (p10) map.set(p10, (map.get(p10) || 0) + 1);
      });
      setUnreadMap(map);
    }, () => setUnreadMap(new Map()));
    return unsub;
  }, [myPhones, unassignedPhones, db, fromCards]);

  return unreadMap;
}

// ─────────────────────────────────────────────────────────────────────────────
// Main panel component
// ─────────────────────────────────────────────────────────────────────────────
interface WhatsAppNotificationPanelProps {
  readOnly?: boolean;
}

export default function WhatsAppNotificationPanel({ readOnly = false }: WhatsAppNotificationPanelProps) {
  const { currentUser, roleId, currentProjectId } = useAuth();
  const db = getFirestore(app);
  const navigate = useNavigate();
  const isSalesManager = String(roleId || '') === 'sales-manager';
  const roleLower = String(roleId || '').toLowerCase();
  // Sales managers and admins can see the whole team's urgent items.
  const canSeeTeam = isSalesManager || roleLower === 'admin' || roleLower === 'super-admin';
  const hasDayAccess = useMyPresenterDayAccess(db, currentUser?.uid, currentProjectId);
  const { canPage } = usePermissions();
  // Waiting visitors: managers, admins, and anyone given the Today page.
  const seesReception = canSeeTeam || canPage('today') || hasDayAccess;

  const [expanded, setExpanded]         = useState(false);
  const [tab, setTab]                   = useState<'urgent' | 'visits' | 'chats'>('urgent');
  const [teamView, setTeamView]         = useState<boolean>(() => {
    try { return localStorage.getItem('crm:myToday:team') === '1'; } catch { return false; }
  });
  const [nowTick, setNowTick]           = useState(() => new Date());
  const [actionError, setActionError]   = useState<string | null>(null);
  const [reassignFor, setReassignFor]   = useState<{ anchor: HTMLElement; item: UrgentItem } | null>(null);
  const [ignoreFor, setIgnoreFor]       = useState<{ anchor: HTMLElement; item: UrgentItem } | null>(null);
  const [resolving, setResolving]       = useState<string | null>(null);
  const [outcomeFor, setOutcomeFor]     = useState<{ anchor: HTMLElement; item: UrgentItem; outcome: 'completed' | 'no-show' | 'rescheduled' } | null>(null);
  const [rescheduleAt, setRescheduleAt] = useState('');
  const [salesUsers, setSalesUsers]     = useState<Array<{ email: string; name: string }>>([]);
  const [selectedConv, setSelectedConv] = useState<Conversation | null>(null);
  const [myLeads, setMyLeads]           = useState<MyLead[]>([]);
  const [myBrokers, setMyBrokers]       = useState<MyBroker[]>([]);
  const [customerMapByProject, setCustomerMapByProject] = useState<Map<string, Map<string, CustomerMapEntry>>>(new Map());
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [contactNames, setContactNames] = useState<Map<string, string>>(new Map());

  // ── Query 1: my assigned leads ──────────────────────────────────────────
  useEffect(() => {
    const email = String(currentUser?.email || '').trim();
    if (!email) { setMyLeads([]); return; }
    const q = query(collection(db, 'leads'), where('assignedTo', '==', email), limit(200));
    const unsub = onSnapshot(q, (snap) => {
      setMyLeads(snap.docs.map((d) => ({
        id: d.id,
        name: String(d.data().name || ''),
        phone10: extractPhone10(d.data()),
        projectId: d.data().projectId || null,
        isUnassigned: false,
      })).filter((l) => !!l.phone10));
    }, () => setMyLeads([]));
    return unsub;
  }, [currentUser?.email, db]);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'brokers'), (snap) => {
      setMyBrokers(
        snap.docs.map((d) => {
          const data = d.data() as Record<string, unknown>;
          const phoneDigits = normalizePhoneDigits(
            String(data.phoneE164 || '').trim() ||
            `${String(data.phoneCountryCode || '').trim()}${String(data.phoneNational || '').trim()}`
          );
          return {
            id: d.id,
            name: String(data.brokerName || data.displayName || '').trim(),
            agencyName: String(data.agencyName || '').trim() || null,
            phone10: phoneDigits.length >= 10 ? phoneDigits.slice(-10) : '',
          };
        }).filter((row) => !!row.phone10)
      );
    }, () => setMyBrokers([]));
    return unsub;
  }, [db]);

  // Names confirmed from the WhatsApp conversation are also used by the full
  // Messages page. Keep this panel on the same source so both headers agree.
  useEffect(() => {
    return onSnapshot(query(collection(db, 'whatsappContacts'), limit(1000)), (snap) => {
      const names = new Map<string, string>();
      snap.docs.forEach((d) => {
        const data = d.data() as Record<string, unknown>;
        const phone = String(data.phone || d.id).replace(/\D/g, '').slice(-10);
        const name = String(data.confirmedName || '').trim();
        if (phone && name) names.set(phone, name);
      });
      setContactNames(names);
    }, () => setContactNames(new Map()));
  }, [db]);

  useEffect(() => {
    let cancelled = false;
    const projectUnsubs: Array<() => void> = [];

    const loadProjectCustomerMap = async (projectId: string, projectName: string) => {
      try {
        const unitsSnap = await getDocs(collection(db, 'projects', projectId, 'units'));
        if (cancelled) return;

        const projectMap = new Map<string, CustomerMapEntry>();
        const targets = unitsSnap.docs.map((d) => {
          const data = d.data() as Record<string, unknown>;
          const unitId = d.id;
          const unitNumber = String(
            data.unitNumber ||
            data.unitNo ||
            data.unit ||
            unitId
          ).trim();
          const directName = buildCustomerDisplayName(data);
          const directPhone = extractCustomerPhoneDigits(data);
          const bookingId = String(data.activeBookingId || data.bookingId || '').trim();
          return { unitId, unitNumber, directName, directPhone, bookingId };
        });

        targets.forEach((row) => {
          if (row.directPhone && row.directName) {
            projectMap.set(row.directPhone.slice(-10), {
              projectId,
              projectName,
              unitId: row.unitId,
              unitNumber: row.unitNumber,
              fullName: row.directName,
            });
          }
        });

        const bookingTargets = targets;
        await Promise.all(
          bookingTargets.map(async (row) => {
            try {
              let bookingId = String(row.bookingId || '').trim();
              if (!bookingId) {
                const bookingsSnap = await getDocs(
                  query(collection(db, 'projects', projectId, 'units', row.unitId, 'bookings'), limit(20))
                );
                const bookingRows: Array<Record<string, unknown>> = bookingsSnap.docs.map((d) => ({ id: d.id, ...(d.data() as Record<string, unknown>) }));
                const toMillis = (value: unknown) =>
                  typeof (value as { toMillis?: () => number } | null)?.toMillis === 'function'
                    ? (value as { toMillis: () => number }).toMillis()
                    : typeof (value as { seconds?: number } | null)?.seconds === 'number'
                    ? Number((value as { seconds: number }).seconds) * 1000
                    : 0;
                const pickBooking = (rows: Array<Record<string, unknown>>) =>
                  rows
                    .slice()
                    .sort((a, b) => {
                      const updatedA = toMillis(a?.updatedAt || a?.createdAt);
                      const updatedB = toMillis(b?.updatedAt || b?.createdAt);
                      if (updatedA !== updatedB) return updatedB - updatedA;
                      return String(b?.id || '').localeCompare(String(a?.id || ''));
                    })[0] || null;
                const activeBookingRow =
                  bookingRows.find((bookingRow) => bookingRow?.isActive === true) ||
                  bookingRows.find((bookingRow) => String(bookingRow?.status || '').trim().toLowerCase() === 'active') ||
                  pickBooking(bookingRows);
                bookingId = String(activeBookingRow?.id || '').trim();
              }
              if (!bookingId) return;

              const customersSnap = await getDocs(
                collection(db, 'projects', projectId, 'units', row.unitId, 'bookings', bookingId, 'customers')
              );
              if (cancelled || customersSnap.empty) return;

              let primaryName = '';
              let fallbackName = '';
              let primaryPhone = '';
              let fallbackPhone = '';

              customersSnap.docs.forEach((customerDoc) => {
                const customer = customerDoc.data() as Record<string, unknown>;
                const name = buildCustomerDisplayName(customer);
                const phoneDigits = extractCustomerPhoneDigits(customer);
                if (!name && !phoneDigits) return;
                const role = String(customer.role || '').trim().toLowerCase();
                if (role === 'primary') {
                  if (!primaryName && name) primaryName = name;
                  if (!primaryPhone && phoneDigits) primaryPhone = phoneDigits;
                }
                if (!fallbackName && name) fallbackName = name;
                if (!fallbackPhone && phoneDigits) fallbackPhone = phoneDigits;
              });

              const fullName = primaryName || fallbackName;
              const phoneDigits = primaryPhone || fallbackPhone;
              if (!phoneDigits || !fullName) return;

              projectMap.set(phoneDigits.slice(-10), {
                projectId,
                projectName,
                unitId: row.unitId,
                unitNumber: row.unitNumber,
                fullName,
              });
            } catch (err) {
              console.error('Failed to load project customer lookup', err);
            }
          })
        );

        if (cancelled) return;
        setCustomerMapByProject((prev) => {
          const next = new Map(prev);
          next.set(projectId, projectMap);
          return next;
        });
      } catch (err) {
        console.error('Failed to load project customer map', err);
      }
    };

    const run = async () => {
      try {
        const projectsSnap = await getDocs(collection(db, 'projects'));
        if (cancelled) return;
        projectsSnap.docs.forEach((projectDoc) => {
          const data = projectDoc.data() as Record<string, unknown>;
          const projectId = projectDoc.id;
          const projectName = String(data.name || data.title || projectId).trim();
          const unsub = onSnapshot(collection(db, 'projects', projectId, 'units'), () => {
            void loadProjectCustomerMap(projectId, projectName);
          });
          projectUnsubs.push(unsub);
          void loadProjectCustomerMap(projectId, projectName);
        });
      } catch (err) {
        console.error('Failed to initialize project customer map', err);
      }
    };

    void run();

    return () => {
      cancelled = true;
      projectUnsubs.forEach((unsub) => unsub());
    };
  }, [db]);

  // ── Query 2: unassigned leads (sales-manager only) ──────────────────────
  const [unassignedLeads, setUnassignedLeads] = useState<MyLead[]>([]);
  useEffect(() => {
    if (!isSalesManager) { setUnassignedLeads([]); return; }
    const q = query(collection(db, 'leads'), where('assignedTo', '==', ''), limit(200));
    const unsub = onSnapshot(q, (snap) => {
      setUnassignedLeads(snap.docs.map((d) => ({
        id: d.id,
        name: String(d.data().name || ''),
        phone10: extractPhone10(d.data()),
        projectId: d.data().projectId || null,
        isUnassigned: true,
      })).filter((l) => !!l.phone10));
    }, () => setUnassignedLeads([]));
    return unsub;
  }, [isSalesManager, db]);

  // phone10 → lead lookup (combined)
  const phoneLeadMap = useMemo(() => {
    const map = new Map<string, MyLead>();
    [...myLeads, ...unassignedLeads].forEach((l) => {
      if (!map.has(l.phone10)) map.set(l.phone10, l);
    });
    return map;
  }, [myLeads, unassignedLeads]);

  const phoneBrokerMap = useMemo(() => {
    const map = new Map<string, MyBroker>();
    myBrokers.forEach((b) => {
      if (!map.has(b.phone10)) map.set(b.phone10, b);
    });
    return map;
  }, [myBrokers]);

  const customerMap = useMemo(() => {
    const merged = new Map<string, CustomerMapEntry>();
    customerMapByProject.forEach((projectMap) => {
      projectMap.forEach((entry, phone10) => {
        if (!merged.has(phone10)) merged.set(phone10, entry);
      });
    });
    return merged;
  }, [customerMapByProject]);

  // ── Sales team: Chats tab from their own chat cards ─────────────────────
  useEffect(() => {
    const email = String(currentUser?.email || '').trim().toLowerCase();
    if (canSeeTeam || !email) return;
    return onSnapshot(
      query(collection(db, 'whatsappChats'), where('assignedTo', '==', email), orderBy('lastMessageAt', 'desc'), limit(30)),
      (snap) => {
        setConversations(snap.docs.map((d) => {
          const c = d.data() as any;
          const p10 = d.id.slice(-10);
          const lead = phoneLeadMap.get(p10);
          const contactName = lead?.name || c.leadName || contactNames.get(p10) || c.fromName || formatPhoneDisplay(d.id);
          const lastMessage = {
            id: `card-${d.id}`, channel: 'whatsapp', contactPhone: d.id,
            direction: c.lastDirection === 'inbound' ? 'inbound' : 'outbound',
            type: 'text', text: String(c.lastPreview || ''), createdAt: c.lastMessageAt || null, readAt: c.lastMessageAt || null,
          } as unknown as ChannelMessage;
          return {
            contactId: normalizeContactId('whatsapp', d.id), channel: 'whatsapp', contactPhone: d.id,
            contactName, contactDisplay: contactName, lastMessage,
            lastMessageAt: c.lastMessageAt?.toDate ? c.lastMessageAt.toDate() : new Date(0),
            unreadCount: Math.max(0, Number(c.unreadCount || 0)),
            leadId: lead?.id || c.leadId || null, leadName: lead?.name || c.leadName || null,
            brokerId: null, brokerName: null, brokerAgencyName: null,
            customerUnitId: null, customerUnitNumber: null, customerProjectId: null, customerProjectName: null, customerName: null,
          } as Conversation;
        }));
      },
      () => setConversations([])
    );
  }, [canSeeTeam, currentUser?.email, db, phoneLeadMap, contactNames]);

  // ── Messages listener for relevant phones ───────────────────────────────
  useEffect(() => {
    if (!canSeeTeam) return;
    const phonesArr = Array.from(phoneLeadMap.keys())
      .concat(Array.from(phoneBrokerMap.keys()))
      .map((p10) => `91${p10}`)
      .slice(0, 30);

    if (phonesArr.length === 0) { setConversations([]); return; }

    const q = query(
      collection(db, 'whatsappMessages'),
      where('contactPhone', 'in', phonesArr),
      orderBy('createdAt', 'desc'),
      limit(500),
    );
    const unsub = onSnapshot(q, (snap) => {
      const convMap = new Map<string, { msgs: ChannelMessage[]; unread: number }>();

      snap.docs.forEach((d) => {
        const data = d.data() as Omit<ChannelMessage, 'id'>;
        const msg: ChannelMessage = { id: d.id, ...data };
        const phone = String(msg.contactPhone || '').trim();
        if (!phone) return;
        const p10 = phone.replace(/\D/g, '').slice(-10);
        const existing = convMap.get(p10);
        if (!existing) {
          convMap.set(p10, {
            msgs: [msg],
            unread: msg.direction === 'inbound' && !msg.readAt ? 1 : 0,
          });
        } else {
          existing.msgs.push(msg);
          if (msg.direction === 'inbound' && !msg.readAt) existing.unread += 1;
        }
      });

      const convs: Conversation[] = [];
      convMap.forEach((val, p10) => {
        const lead    = phoneLeadMap.get(p10);
        const broker  = phoneBrokerMap.get(p10);
        const customer = customerMap.get(p10);
        const lastMsg = val.msgs[0];
        const phone12 = `91${p10}`;
        const brokerDisplay = broker ? formatBrokerDisplayName(broker.name, broker.agencyName) : null;
        const contactName = lead?.name || brokerDisplay || customer?.fullName || contactNames.get(p10) || lastMsg.fromName || formatPhoneDisplay(phone12);
        convs.push({
          contactId:      normalizeContactId('whatsapp', phone12),
          channel:        'whatsapp',
          contactPhone:   phone12,
          contactName,
          contactDisplay: contactName,
          lastMessage:    lastMsg,
          lastMessageAt:  lastMsg.createdAt ? lastMsg.createdAt.toDate() : new Date(0),
          unreadCount:    val.unread,
          leadId:         lead?.id || null,
          leadName:       lead?.name || null,
          brokerId:       broker?.id || null,
          brokerName:     broker?.name || null,
          brokerAgencyName: broker?.agencyName || null,
          customerUnitId: customer?.unitId || null,
          customerUnitNumber: customer?.unitNumber || null,
          customerProjectId: customer?.projectId || null,
          customerProjectName: customer?.projectName || null,
          customerName: customer?.fullName || null,
        });
      });

      convs.sort((a, b) => b.lastMessageAt.getTime() - a.lastMessageAt.getTime());
      setConversations(convs);
    }, () => setConversations([]));

    return unsub;
  }, [customerMap, phoneBrokerMap, phoneLeadMap, contactNames, db, canSeeTeam]);

  const totalUnread = useMemo(
    () => conversations.reduce((sum, c) => sum + c.unreadCount, 0),
    [conversations],
  );

  const urgentAll = useMyTodayUrgent(db, String(currentUser?.email || ''), canSeeTeam, canSeeTeam && teamView);
  // Visits live in their own tab; one comes back to Urgent only when its
  // outcome is still not recorded an hour after the visit time.
  const urgent = useMemo(
    () => urgentAll.filter((u) => needsActionNow(u, nowTick)),
    [urgentAll, nowTick],
  );
  const upcoming = useMemo(() => urgentAll.filter((u) => u.bucket === 'tomorrow' && u.kind !== 'visit'), [urgentAll]);
  // Done / didn't come / cancelled visits leave the panel (the Today page keeps them).
  const visitsToday = useMemo(
    () => urgentAll.filter((u) => u.kind === 'visit' && u.visitDay === 'today' && !['completed', 'no-show', 'cancelled'].includes(String(u.visitStatus || 'scheduled'))),
    [urgentAll],
  );
  const visitsTomorrow = useMemo(() => urgentAll.filter((u) => u.kind === 'visit' && u.bucket === 'tomorrow'), [urgentAll]);
  const visitCount = useMemo(() => visitsToday.filter((v) => v.visitStatus !== 'cancelled').length, [visitsToday]);
  const [showTomorrow, setShowTomorrow] = useState(false);
  const [showVisitsTomorrow, setShowVisitsTomorrow] = useState(false);
  // Keep an active meeting visible to its presenter if today's grant is later removed.
  const reception = useReceptionToday(db);
  const myEmailLower = String(currentUser?.email || '').toLowerCase();
  const waiting = useMemo(() => seesReception ? reception.filter((r) =>
    r.status === 'waiting' && (canSeeTeam || canPage('today') || r.projectId === currentProjectId)
  ) : [], [reception, seesReception, canSeeTeam, canPage, currentProjectId]);
  const withTeam = useMemo(() => reception.filter((r) => r.status === 'with' && (seesReception || String(r.presenterEmail || '').toLowerCase() === myEmailLower)), [reception, seesReception, myEmailLower]);
  const myPresenting = useMemo(
    () => withTeam.filter((r) => String(r.presenterEmail || '').toLowerCase() === myEmailLower),
    [withTeam, myEmailLower],
  );
  const waitingCount = waiting.length;
  const [seenWaitingIds, setSeenWaitingIds] = useState<Set<string>>(new Set());
  const [takingId, setTakingId] = useState<string | null>(null);
  const [finishFor, setFinishFor] = useState<ReceptionVisit | null>(null);
  const urgentCount = urgent.length;
  const overdueCount = useMemo(() => urgent.filter((u) => isOverdue(u, nowTick)).length, [urgent, nowTick]);

  useEffect(() => {
    const timer = window.setInterval(() => setNowTick(new Date()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    try { localStorage.setItem('crm:myToday:team', teamView ? '1' : '0'); } catch { /* ignore */ }
  }, [teamView]);

  // People who can own a lead, for Reassign (managers and admins only).
  useEffect(() => {
    if (!canSeeTeam) { setSalesUsers([]); return; }
    return onSnapshot(collection(db, 'users'), (snap) => {
      setSalesUsers(
        snap.docs
          .map((d) => d.data() as any)
          .filter((u) => ['sales-team', 'sales-manager', 'admin', 'super-admin'].includes(String(u.roleId || u.role || '').toLowerCase()) && u.email)
          .map((u) => ({ email: String(u.email).toLowerCase(), name: String(u.displayName || u.name || u.email) }))
          .sort((a, b) => a.name.localeCompare(b.name))
      );
    }, () => setSalesUsers([]));
  }, [canSeeTeam, db]);

  // Auto-collapse only when nothing is unread or urgent AND no conversation is open
  // (if user is actively viewing a chat, don't close it mid-conversation)
  useEffect(() => {
    if (totalUnread === 0 && urgentCount === 0 && visitCount === 0 && waitingCount === 0 && myPresenting.length === 0 && selectedConv === null) {
      setExpanded(false);
    }
  }, [totalUnread, urgentCount, visitCount, waitingCount, myPresenting.length, selectedConv]);

  // A new visitor at reception opens the panel by itself.
  useEffect(() => {
    const fresh = waiting.filter((r) => !seenWaitingIds.has(r.id));
    if (!fresh.length) return;
    setSeenWaitingIds((prev) => new Set([...Array.from(prev), ...fresh.map((r) => r.id)]));
    setSelectedConv(null);
    setExpanded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waiting]);

  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleOpenConv = useCallback((conv: Conversation) => {
    if (!readOnly) setSelectedConv(conv);
  }, [readOnly]);

  useEffect(() => {
    const openRequestedConversation = (event: Event) => {
      if (readOnly) return;

      const detail = (event as CustomEvent<OpenWhatsAppPanelDetail>).detail;
      const phone10 = String(detail?.phone10 || '').replace(/\D/g, '').slice(-10);
      if (phone10.length !== 10) return;

      const phone12 = `91${phone10}`;
      const contactId = normalizeContactId('whatsapp', phone12);
      const lead = phoneLeadMap.get(phone10);
      const broker = phoneBrokerMap.get(phone10);
      const customer = customerMap.get(phone10);
      const knownName = customer?.fullName || lead?.name ||
        (broker ? formatBrokerDisplayName(broker.name, broker.agencyName) : null) ||
        contactNames.get(phone10);
      const existing = conversations.find((conversation) => {
        const conversationPhone10 = String(conversation.contactPhone || '').replace(/\D/g, '').slice(-10);
        return conversation.contactId === contactId || conversationPhone10 === phone10;
      });

      setSelectedConv(existing ? {
        ...existing,
        leadId: existing.leadId || detail.leadId || null,
        brokerId: existing.brokerId || detail.brokerId || null,
        contactName: knownName || existing.contactName || detail.name || existing.contactDisplay,
        contactDisplay: knownName || existing.contactDisplay || detail.name || formatPhoneDisplay(phone12),
      } : {
        contactId,
        channel: 'whatsapp',
        contactPhone: phone12,
        contactName: knownName || detail.name || formatPhoneDisplay(phone12),
        contactDisplay: knownName || detail.name || formatPhoneDisplay(phone12),
        lastMessage: {} as ChannelMessage,
        lastMessageAt: new Date(),
        unreadCount: 0,
        leadId: detail.leadId || null,
        leadName: detail.name || null,
        brokerId: detail.brokerId || null,
      });
      setTab('chats');
      setExpanded(true);
    };

    window.addEventListener(OPEN_WHATSAPP_PANEL_EVENT, openRequestedConversation);
    return () => window.removeEventListener(OPEN_WHATSAPP_PANEL_EVENT, openRequestedConversation);
  }, [contactNames, conversations, customerMap, phoneBrokerMap, phoneLeadMap, readOnly]);

  // Name sources can arrive just after the panel opens. Refresh the active
  // header without making the user close and reopen the conversation. If this
  // is an unlinked WhatsApp contact, use the same message `fromName` fallback
  // as the full Messages page.
  useEffect(() => {
    if (!selectedConv?.contactPhone) return;
    const phone10 = String(selectedConv.contactPhone).replace(/\D/g, '').slice(-10);
    const lead = phoneLeadMap.get(phone10);
    const broker = phoneBrokerMap.get(phone10);
    const customer = customerMap.get(phone10);
    const knownName = customer?.fullName || lead?.name ||
      (broker ? formatBrokerDisplayName(broker.name, broker.agencyName) : null) ||
      contactNames.get(phone10);

    if (knownName) {
      if (knownName === selectedConv.contactDisplay) return;
      setSelectedConv((current) => current ? {
        ...current,
        contactName: knownName,
        contactDisplay: knownName,
      } : current);
      return;
    }

    let cancelled = false;
    const phone12 = `91${phone10}`;
    void getDocs(query(
      collection(db, 'whatsappMessages'),
      where('contactPhone', '==', phone12),
      orderBy('createdAt', 'desc'),
      limit(30),
    )).then((snap) => {
      if (cancelled) return;
      const messageName = snap.docs
        .map((row) => String(row.data().fromName || '').trim())
        .find(Boolean);
      if (!messageName) return;
      setSelectedConv((current) => current &&
        String(current.contactPhone || '').replace(/\D/g, '').slice(-10) === phone10
        ? { ...current, contactName: messageName, contactDisplay: messageName }
        : current);
    }).catch(() => {});

    return () => { cancelled = true; };
  }, [contactNames, customerMap, db, phoneBrokerMap, phoneLeadMap, selectedConv?.contactDisplay, selectedConv?.contactPhone]);

  const handleBack     = useCallback(() => setSelectedConv(null), []);
  const handleOpenFull = useCallback(() => {
    setExpanded(false);
    setSelectedConv(null);
    navigate('/messages');
  }, [navigate]);
  const handleClose    = useCallback(() => { setExpanded(false); setSelectedConv(null); }, []);
  // Open the full MessagingInboxPage with the conversation pre-selected via ?chat= param
  const handleOpenLead = useCallback(
    (contactId?: string | null) => {
      setExpanded(false);
      setSelectedConv(null);
      navigate(contactId ? `/messages?chat=${encodeURIComponent(contactId)}` : '/messages');
    },
    [navigate],
  );
  const handleOpenBroker = useCallback(
    (brokerId?: string | null) =>
      navigate(brokerId ? `/brokers?brokerId=${encodeURIComponent(brokerId)}` : '/brokers'),
    [navigate],
  );
  const handleOpenCustomerUnit = useCallback(
    (unitId?: string | null) =>
      window.open(
        unitId ? `/office/units/${encodeURIComponent(unitId)}` : '/office/units',
        '_blank',
        'noopener,noreferrer'
      ),
    [],
  );

  const openUrgentChat = (item: UrgentItem) => {
    if (readOnly) return;
    const phone12 = `91${item.phone10}`;
    setSelectedConv({
      contactId: normalizeContactId('whatsapp', phone12),
      channel: 'whatsapp',
      contactPhone: phone12,
      contactName: item.name,
      contactDisplay: item.name,
      lastMessage: {} as ChannelMessage,
      lastMessageAt: new Date(),
      unreadCount: 0,
      leadId: item.leadId,
      leadName: item.name,
    });
  };

  // Done, Reopen and Ignore were being tapped by mistake: each now asks once more.
  const [confirm, confirmDialog] = useConfirmAction();
  const subjectOf = (item: UrgentItem) => `${displayName(item.name, item.phone10)}${item.label ? `: ${item.label}` : ''}`;
  const confirmDone = async (item: UrgentItem) => {
    if (readOnly) return;
    if (await confirm({ title: 'Mark this as done?', subject: subjectOf(item), body: 'It leaves the Today list.', confirmLabel: 'Yes, mark done' })) markDone(item);
  };
  const confirmReopen = async (item: UrgentItem) => {
    if (readOnly) return;
    const take = !item.ownerEmail;
    if (await confirm({ title: take ? 'Reopen this lead and take it?' : 'Reopen this lead?', subject: subjectOf(item), body: take ? 'The lead opens again and is assigned to you.' : 'The lead opens again for its owner.', confirmLabel: take ? 'Yes, reopen and take it' : 'Yes, reopen', tone: 'gold' })) {
      resolveReengagement(item, 'reopen', take);
    }
  };
  const confirmIgnore = async (item: UrgentItem, key: string) => {
    if (readOnly) return;
    setIgnoreFor(null);
    const reason = IGNORE_REASONS.find((r) => r.key === key)?.label || key;
    if (await confirm({ title: 'Ignore this message?', subject: subjectOf(item), body: `Reason: ${reason}.`, confirmLabel: 'Yes, ignore', tone: 'danger' })) {
      resolveReengagement(item, key);
    }
  };

  const markDone = async (item: UrgentItem) => {
    setActionError(null);
    try {
      await markItemDone(db, item, currentUser?.email || null);
    } catch (err: any) {
      setActionError(`Could not mark done: ${err?.message || err}`);
    }
  };

  const resolveReengagement = async (item: UrgentItem, decision: string, assignToMe = false) => {
    setIgnoreFor(null);
    if (!item.leadId) return;
    setActionError(null);
    setResolving(item.id);
    try {
      await resolveLeadReengagement(item.leadId, decision, assignToMe);
    } catch (err: any) {
      setActionError(`Could not update: ${err?.message || err}`);
    } finally {
      setResolving(null);
    }
  };

  // Visit outcome from the row. Customer WhatsApp (thank-you, missed visit,
  // new time) goes out only because the team marked it here.
  const markVisit = async (item: UrgentItem, outcome: 'completed' | 'no-show' | 'rescheduled', newAt?: Date) => {
    if (readOnly) return;
    setOutcomeFor(null);
    if (!item.leadId) return;
    setActionError(null);
    setResolving(item.id);
    const by = currentUser?.email || null;
    const leadRef = doc(db, 'leads', item.leadId);
    try {
      const snap = await getDoc(leadRef);
      const lead = (snap.data() || {}) as Record<string, any>;
      const appt = lead.appointment || {};
      if (outcome === 'rescheduled') {
        if (!newAt || Number.isNaN(newAt.getTime()) || newAt.getTime() < Date.now()) {
          setActionError('Pick a future date and time.');
          return;
        }
        const ts = Timestamp.fromDate(newAt);
        await updateDoc(leadRef, {
          appointment: {
            ...appt, scheduledAt: ts, status: 'scheduled',
            customerConfirmedAt: null, customerRunningLateAt: null, movedOnWhatsappAt: null,
            updatedAt: serverTimestamp(), updatedBy: by,
          },
          nextFollowUpDate: ts, nextFollowUpAt: ts,
        });
        await addDoc(collection(db, 'leads', item.leadId, 'activities'), {
          type: 'internal', event: 'appointment-rescheduled', appointmentType: 'site-visit',
          appointmentAt: ts, appointmentOutcome: 'rescheduled',
          notes: ['Visit rescheduled from My Today.'], byEmail: by, at: serverTimestamp(),
        });
        return;
      }
      const tomorrow10 = new Date();
      tomorrow10.setDate(tomorrow10.getDate() + 1);
      tomorrow10.setHours(10, 0, 0, 0);
      const updates: Record<string, any> = {
        appointment: {
          ...appt, status: outcome, outcome, completedAt: serverTimestamp(),
          updatedAt: serverTimestamp(), updatedBy: by,
        },
      };
      if (outcome === 'completed') {
        updates.visitCount = increment(1);
        updates.nextFollowUpDate = Timestamp.fromDate(tomorrow10);
        updates.nextFollowUpAt = null;
        // Move forward to Site Visited only from the earlier stages.
        if (VISITED_FROM.has(String(lead.status || ''))) updates.status = SITE_VISITED_STATUS;
      }
      await updateDoc(leadRef, updates);
      await addDoc(collection(db, 'leads', item.leadId, 'activities'), outcome === 'completed'
        ? {
          type: 'visit', appointmentType: 'site-visit', appointmentAt: appt.scheduledAt || null,
          appointmentOutcome: 'completed',
          statusFrom: updates.status ? lead.status : null, statusTo: updates.status || null,
          notes: ['Marked visited from My Today.'], byEmail: by, at: serverTimestamp(),
        }
        : {
          type: 'internal', event: 'appointment-completed', appointmentType: 'site-visit',
          appointmentAt: appt.scheduledAt || null, appointmentOutcome: 'no-show',
          notes: ["Marked didn't come from My Today."], byEmail: by, at: serverTimestamp(),
        });
    } catch (err: any) {
      setActionError(`Could not update the visit: ${err?.message || err}`);
    } finally {
      setResolving(null);
    }
  };

  const takeVisitor = async (r: ReceptionVisit) => {
    if (readOnly) return;
    if (!currentUser?.email || !(canSeeTeam || (hasDayAccess && r.projectId === currentProjectId))) return;
    setActionError(null);
    setTakingId(r.id);
    const email = currentUser.email;
    const name = salesUsers.find((u) => u.email === email.toLowerCase())?.name || currentUser.displayName || email;
    try {
      await takeReceptionVisit(db, r, { email, name });
    } catch (err: any) {
      setActionError(err?.message || String(err));
    } finally {
      setTakingId(null);
    }
  };

  const openVisitLog = (leadId: string) => navigate(`/follow-ups?leadId=${encodeURIComponent(leadId)}&log=visit`);

  const reassign = async (item: UrgentItem, user: { email: string; name: string }) => {
    if (readOnly) return;
    setReassignFor(null);
    if (!item.leadId) return;
    try {
      await updateDoc(doc(db, 'leads', item.leadId), { assignedTo: user.email, assignedToName: user.name });
      await addDoc(collection(db, 'leads', item.leadId, 'activities'), {
        type: 'internal', event: 'lead-reassigned', notes: [`Reassigned to ${user.name} from My Today.`],
        byEmail: currentUser?.email || null, at: serverTimestamp(),
      });
    } catch (err: any) {
      setActionError(`Could not reassign: ${err?.message || err}`);
    }
  };

  // ── Don't render when nothing is unread or urgent AND panel is not in use ──
  if (!currentUser || (totalUnread === 0 && urgentCount === 0 && visitCount === 0 && waitingCount === 0 && myPresenting.length === 0 && !expanded)) return null;

  // ─── Shared JSX: header ────────────────────────────────────────────────
  const panelHeader = (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        px: 1.5, py: 1, minHeight: 48,
        bgcolor: selectedConv ? '#fff' : HEADER_BG,
        color: selectedConv ? DARK : '#fff',
        userSelect: 'none',
        flexShrink: 0,
      }}
    >
      {selectedConv ? (
        <IconButton size="small" onClick={handleBack} sx={{ color: DARK, p: 0.25, mr: 0.25 }}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
      ) : (
        <NotificationsActiveOutlinedIcon sx={{ fontSize: 20, color: '#fff' }} />
      )}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{ fontFamily: FONT_BODY, fontWeight: 600, fontSize: 14, color: selectedConv ? DARK : '#fff', lineHeight: 1.2 }}>
            {selectedConv ? selectedConv.contactDisplay : 'My Today'}
          </Typography>
          {selectedConv?.customerUnitId && (
            <Tooltip
              title={
                <Box sx={{ p: 0.25 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, lineHeight: 1.3 }}>
                    {selectedConv.customerName || selectedConv.contactDisplay || 'Customer'}
                  </Typography>
                  <Typography sx={{ fontSize: 11, lineHeight: 1.3 }}>
                    Project: {selectedConv.customerProjectName || '—'}
                  </Typography>
                  <Typography sx={{ fontSize: 11, lineHeight: 1.3 }}>
                    Unit: {selectedConv.customerUnitNumber || '—'}
                  </Typography>
                </Box>
              }
            >
              <Box sx={{ bgcolor: 'rgba(14,165,233,0.14)', color: '#0284C7', borderRadius: 999, px: 0.8, py: 0.2, fontSize: 10, fontWeight: 700, lineHeight: 1.2, flexShrink: 0 }}>
                Customer
              </Box>
            </Tooltip>
          )}
        </Stack>
        {selectedConv && (
          <Typography noWrap sx={{ fontFamily: FONT_BODY, fontSize: 12, color: MUTED, lineHeight: 1.2 }}>
            {formatPhoneDisplay(selectedConv.contactPhone || '')}
          </Typography>
        )}
        {!selectedConv && (urgentCount > 0 || totalUnread > 0 || visitCount > 0 || waitingCount > 0) && (
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: 11, color: 'rgba(255,255,255,0.85)', lineHeight: 1.2 }}>
            {[
              waitingCount ? `${waitingCount} waiting` : '',
              urgentCount ? `${urgentCount} urgent${overdueCount ? ` (${overdueCount} overdue)` : ''}` : '',
              visitCount ? `${visitCount} visit${visitCount === 1 ? '' : 's'} today` : '',
              totalUnread ? `${totalUnread} unread` : '',
            ].filter(Boolean).join(' · ')}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
        {selectedConv ? (
          <Tooltip title="Open full chat">
            <IconButton size="small" onClick={() => handleOpenLead(selectedConv.contactId)} sx={{ color: MUTED, p: 0.25 }}>
              <OpenInNewIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Open Messages page">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenFull(); }} sx={{ color: 'rgba(255,255,255,0.8)', p: 0.25 }}>
              <OpenInNewIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        {selectedConv?.customerUnitId && (String(roleId || '').toLowerCase() === 'admin' || String(roleId || '').toLowerCase() === 'super-admin') && (
          <Tooltip title={`Open Unit${selectedConv.customerProjectName ? ` · ${selectedConv.customerProjectName}` : ''}`}>
            <IconButton
              size="small"
              onClick={() => handleOpenCustomerUnit(selectedConv.customerUnitId)}
              sx={{ color: '#0284C7', p: 0.25 }}
            >
              <StorefrontOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        <IconButton size="small" onClick={handleClose} sx={{ color: selectedConv ? MUTED : 'rgba(255,255,255,0.8)', p: 0.25 }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </Box>
  );

  // ─── Shared JSX: conversation list ────────────────────────────────────
  const conversationList = (
    <Box sx={{ overflowY: 'auto', flex: 1 }}>
      {conversations.filter((c) => c.unreadCount > 0).length === 0 ? (
        <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
          <WhatsAppIcon sx={{ fontSize: 32, color: '#ccc', mb: 0.5 }} />
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: 13, color: MUTED }}>
            All caught up
          </Typography>
        </Box>
      ) : (
        conversations
          .filter((c) => c.unreadCount > 0)
          .map((conv, idx, arr) => {
            const p10     = (conv.contactPhone || '').replace(/\D/g, '').slice(-10);
            const lead    = phoneLeadMap.get(p10);
            const avStyle = avatarStyle(conv.contactPhone || '');
            const init    = initials(conv.contactDisplay);
            return (
              <React.Fragment key={conv.contactId}>
                <Box
                  onClick={() => handleOpenConv(conv)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    px: 2, py: 1.25,
                    cursor: 'pointer',
                    bgcolor: 'rgba(37,211,102,0.04)',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                    transition: 'background 0.12s',
                  }}
                >
                  <Badge
                    overlap="circular"
                    badgeContent={conv.unreadCount}
                    max={99}
                    sx={{ '& .MuiBadge-badge': { bgcolor: WA_GREEN, color: '#fff', fontWeight: 800, fontSize: 10, minWidth: 16, height: 16 } }}
                  >
                    <Avatar sx={{ width: 40, height: 40, bgcolor: avStyle.bg, color: avStyle.color, fontSize: 14, fontWeight: 600, fontFamily: FONT_BODY }}>
                      {init}
                    </Avatar>
                  </Badge>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.2 }}>
                      <Typography noWrap sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: 14, color: DARK, flex: 1 }}>
                        {conv.contactDisplay}
                      </Typography>
                      {conv.customerUnitId && (
                        <Tooltip
                          title={
                            <Box sx={{ p: 0.25 }}>
                              <Typography sx={{ fontSize: 12, fontWeight: 700, lineHeight: 1.3 }}>
                                {conv.customerName || conv.contactDisplay || 'Customer'}
                              </Typography>
                              <Typography sx={{ fontSize: 11, lineHeight: 1.3 }}>
                                Project: {conv.customerProjectName || '—'}
                              </Typography>
                              <Typography sx={{ fontSize: 11, lineHeight: 1.3 }}>
                                Unit: {conv.customerUnitNumber || '—'}
                              </Typography>
                            </Box>
                          }
                        >
                          <Box sx={{ bgcolor: 'rgba(14,165,233,0.14)', color: '#0284C7', fontSize: 9, fontWeight: 700, px: 0.6, py: 0.1, borderRadius: 1, lineHeight: 1.6, flexShrink: 0, border: '1px solid rgba(14,165,233,0.18)' }}>
                            CUSTOMER
                          </Box>
                        </Tooltip>
                      )}
                      {lead?.isUnassigned && (
                        <Box sx={{ bgcolor: '#FFF3CD', color: '#856404', fontSize: 9, fontWeight: 700, px: 0.6, py: 0.1, borderRadius: 1, lineHeight: 1.6, flexShrink: 0, border: '1px solid #FFEEBA' }}>
                          UNASSIGNED
                        </Box>
                      )}
                      <Typography sx={{ fontFamily: FONT_BODY, fontSize: 11, color: MUTED, flexShrink: 0 }}>
                        {formatMessageTimestamp(conv.lastMessage?.createdAt)}
                      </Typography>
                    </Box>
                    <Typography noWrap sx={{ fontFamily: FONT_BODY, fontSize: 13, color: DARK, fontWeight: 500 }}>
                      {conv.lastMessage?.text || '(media)'}
                    </Typography>
                  </Box>
                </Box>
                {idx < arr.length - 1 && <Divider sx={{ mx: 2 }} />}
              </React.Fragment>
            );
          })
      )}
    </Box>
  );

  // ─── Shared JSX: tabs (Urgent | Chats) + Mine/Team ─────────────────────
  const tabBar = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.75, borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
      {([
        { key: 'urgent', label: `Urgent${urgentCount ? ` (${urgentCount})` : ''}` },
        { key: 'visits', label: `Visits${visitCount ? ` (${visitCount})` : ''}` },
        { key: 'chats', label: `Chats${totalUnread ? ` (${totalUnread})` : ''}` },
      ] as const).map((t) => (
        <Box
          key={t.key}
          component="button"
          type="button"
          onClick={() => setTab(t.key)}
          sx={{
            fontFamily: FONT_BODY, fontSize: 12.5, fontWeight: tab === t.key ? 700 : 500,
            color: tab === t.key ? DARK : MUTED, bgcolor: tab === t.key ? '#f3efe8' : 'transparent',
            border: 'none', borderRadius: 999, px: 1.25, height: 26, cursor: 'pointer',
          }}
        >
          {t.label}
        </Box>
      ))}
      <Box sx={{ flex: 1 }} />
      {canSeeTeam && tab !== 'chats' && (
        <Box sx={{ display: 'flex', border: `1px solid ${BORDER}`, borderRadius: 999, p: 0.25 }}>
          {([false, true] as const).map((team) => (
            <Box
              key={String(team)}
              component="button"
              type="button"
              onClick={() => setTeamView(team)}
              sx={{
                fontFamily: FONT_BODY, fontSize: 11.5, fontWeight: teamView === team ? 700 : 500,
                color: teamView === team ? '#fff' : MUTED, bgcolor: teamView === team ? GOLD : 'transparent',
                border: 'none', borderRadius: 999, px: 1, height: 22, cursor: 'pointer',
              }}
            >
              {team ? 'Team' : 'Mine'}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );

  // ─── Shared JSX: urgent list ──────────────────────────────────────────
  const kindIcon = (item: UrgentItem) =>
    item.kind === 'reengaged' ? '🔁' : item.kind === 'callback' || item.kind === 'visitTask' || item.kind === 'visitMessageFailure' ? '📞' : item.kind === 'visit' || item.kind === 'visitRequest' ? '📍' : '🙋';
  const pillSx = { fontFamily: FONT_BODY, fontSize: 11.5, fontWeight: 600, bgcolor: 'transparent', borderRadius: 999, px: 1, cursor: 'pointer', whiteSpace: 'nowrap' } as const;
  const OUTCOME_NOTE: Record<'completed' | 'no-show' | 'rescheduled', string> = {
    completed: 'Marks the visit done and moves the lead to Site Visited. The customer gets a thank-you on WhatsApp this evening.',
    'no-show': "Marks it as didn't come. The customer gets a \"Sorry we missed you\" WhatsApp with buttons to pick a new time, and you get a call task for tomorrow 10 AM.",
    rescheduled: 'The customer gets the new time on WhatsApp, then the usual reminders.',
  };
  const menus = renderMenus();
  const followUpsLink = (
    <Box sx={{ px: 2, py: 1, borderTop: `1px solid ${BORDER}` }}>
      <Box component="button" type="button" onClick={() => navigate('/follow-ups')}
        sx={{ fontFamily: FONT_BODY, fontSize: 12, color: GOLD, bgcolor: 'transparent', border: 'none', p: 0, cursor: 'pointer' }}>
        See all follow-ups →
      </Box>
    </Box>
  );
  const urgentList = (
    <Box sx={{ overflowY: 'auto', flex: 1 }}>
      {actionError && (
        <Typography sx={{ fontFamily: FONT_BODY, fontSize: 12, color: RED, px: 2, pt: 1 }}>{actionError}</Typography>
      )}
      {urgent.length === 0 && upcoming.length === 0 ? (
        <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 30, color: '#ccc', mb: 0.5 }} />
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: 13, color: MUTED }}>Nothing urgent today</Typography>
        </Box>
      ) : (
        <>
        {urgent.length === 0 && (
          <>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: 12.5, color: MUTED, px: 2, py: 1.25 }}>Nothing due today.</Typography>
            <Box component="button" type="button" onClick={() => setShowTomorrow((v) => !v)}
              sx={{ display: 'block', width: '100%', textAlign: 'left', fontFamily: FONT_BODY, fontSize: 12, fontWeight: 700, color: MUTED, bgcolor: '#faf8f4', border: 'none', borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, px: 2, py: 0.75, cursor: 'pointer' }}>
              {showTomorrow ? '▾' : '▸'} Tomorrow ({upcoming.length})
            </Box>
          </>
        )}
        {[...urgent, ...(showTomorrow ? upcoming : [])].map((item, idx, list) => {
          return (
            <React.Fragment key={item.id}>
              {itemRow(item)}
              {idx === urgent.length - 1 && upcoming.length > 0 && (
                <Box component="button" type="button" onClick={() => setShowTomorrow((v) => !v)}
                  sx={{ display: 'block', width: '100%', textAlign: 'left', fontFamily: FONT_BODY, fontSize: 12, fontWeight: 700, color: MUTED, bgcolor: '#faf8f4', border: 'none', borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, px: 2, py: 0.75, cursor: 'pointer' }}>
                  {showTomorrow ? '▾' : '▸'} Tomorrow ({upcoming.length})
                </Box>
              )}
              {idx < list.length - 1 && !(idx === urgent.length - 1 && upcoming.length > 0) && <Divider sx={{ mx: 2 }} />}
            </React.Fragment>
          );
        })}
        </>
      )}
      {menus}
      {followUpsLink}
    </Box>
  );

  // ─── Shared JSX: one Urgent / Visits row ──────────────────────────────
  function itemRow(item: UrgentItem) {
          const overdue = isOverdue(item, nowTick);
          const ownerText = !item.ownerEmail ? 'Unassigned' : item.ownerName || item.ownerEmail;
          const mine = String(item.ownerEmail || '').toLowerCase() === String(currentUser?.email || '').toLowerCase();
          const phase = item.kind === 'visit' ? visitPhase(item, nowTick) : null;
          const closedVisit = phase === 'done' || phase === 'no-show' || phase === 'cancelled';
          const phaseColor = phase === 'done' ? '#2f6b35' : phase === 'now' || phase === 'soon' ? GOLD : overdue ? RED : MUTED;
          return (
              <Box sx={{ display: 'flex', gap: 1.25, px: 2, py: 1.1, bgcolor: overdue ? 'rgba(198,40,40,0.04)' : 'transparent', opacity: closedVisit ? 0.6 : 1 }}>
                <Box sx={{ fontSize: 18, lineHeight: 1.4 }}>{kindIcon(item)}</Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography noWrap sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: 13.5, color: DARK, flex: 1 }}>{displayName(item.name, item.phone10)}</Typography>
                    <Typography sx={{ fontFamily: FONT_BODY, fontSize: 11, fontWeight: 600, color: phaseColor, flexShrink: 0 }}>
                      {dueText(item, nowTick)}
                    </Typography>
                  </Box>
                  <Typography noWrap sx={{ fontFamily: FONT_BODY, fontSize: 12.5, color: DARK }}>
                    {item.label}
                    {item.bookedOnWhatsapp ? ' · booked on WhatsApp' : ''}
                  </Typography>
                  {/* Running late already shows on the right. */}
                  {item.customerNote && !closedVisit && !dueText(item, nowTick).startsWith('Running late') && (
                    <Typography sx={{ fontFamily: FONT_BODY, fontSize: 11.5, color: '#2f6b35' }}>{item.customerNote}</Typography>
                  )}
                  {item.closeInfo && (
                    <Typography sx={{ fontFamily: FONT_BODY, fontSize: 11, color: '#8a5a12' }}>
                      {item.closeInfo}{item.possiblePitch ? ' · Possibly a sales pitch' : ''}
                    </Typography>
                  )}
                  {(!mine || !item.ownerEmail) && (
                    <Typography sx={{ fontFamily: FONT_BODY, fontSize: 11, color: item.ownerEmail ? MUTED : '#856404' }}>{ownerText}</Typography>
                  )}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', rowGap: 0.5, gap: 0.25, mt: 0.5, ml: -0.5 }}>
                    <Tooltip title="Call">
                      <IconButton size="small" component="a" href={`tel:+91${item.phone10}`} sx={{ color: MUTED }}>
                        <PhoneOutlinedIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Open WhatsApp chat">
                      <IconButton size="small" onClick={() => openUrgentChat(item)} sx={{ color: MUTED }}>
                        <ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    {item.leadId && (
                      <Tooltip title="Open lead">
                        <IconButton size="small" onClick={() => navigate(`/follow-ups?leadId=${encodeURIComponent(item.leadId || '')}`)} sx={{ color: MUTED }}>
                          <PersonOutlineIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canSeeTeam && item.leadId && (
                      <Tooltip title="Reassign">
                        <IconButton size="small" onClick={(e) => setReassignFor({ anchor: e.currentTarget, item })} sx={{ color: MUTED }}>
                          <SwapHorizIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Box sx={{ flex: 1 }} />
                    {item.kind === 'reengaged' ? (
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Box component="button" type="button" disabled={resolving === item.id}
                          onClick={() => confirmReopen(item)}
                          sx={{ fontFamily: FONT_BODY, fontSize: 11.5, fontWeight: 700, color: '#fff', bgcolor: GOLD, border: 'none', borderRadius: 999, px: 1.1, cursor: 'pointer' }}>
                          {resolving === item.id ? '…' : item.ownerEmail ? 'Reopen' : 'Reopen & take it'}
                        </Box>
                        <Box component="button" type="button" disabled={resolving === item.id}
                          onClick={(e) => setIgnoreFor({ anchor: e.currentTarget, item })}
                          sx={{ fontFamily: FONT_BODY, fontSize: 11.5, fontWeight: 600, color: MUTED, bgcolor: 'transparent', border: `1px solid ${BORDER}`, borderRadius: 999, px: 1, cursor: 'pointer' }}>
                          Ignore ▾
                        </Box>
                      </Box>
                    ) : closedVisit ? null : item.kind === 'visit' ? (
                      // Visit day: no button; the front desk check-in and the
                      // presenter's Finish visit mark it (status on the right).
                      // From the next day, still unmarked: Didn't come or
                      // Reschedule. Later days: nothing here.
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {item.visitDay === 'past' && (
                          <>
                            <Box component="button" type="button" disabled={resolving === item.id}
                              onClick={(e) => setOutcomeFor({ anchor: e.currentTarget, item, outcome: 'no-show' })}
                              sx={{ ...pillSx, color: RED, border: '1px solid #f1c9c9' }}>
                              ✗ Didn't come
                            </Box>
                            <Box component="button" type="button" disabled={resolving === item.id}
                              onClick={(e) => { setRescheduleAt(''); setOutcomeFor({ anchor: e.currentTarget, item, outcome: 'rescheduled' }); }}
                              sx={{ ...pillSx, color: GOLD, border: `1px solid ${GOLD}55` }}>
                              ↻ Reschedule
                            </Box>
                          </>
                        )}
                      </Box>
                    ) : item.kind === 'visitRequest' ? (
                      <Tooltip title="Schedule the visit in the lead">
                        <Box component="button" type="button" onClick={() => navigate(`/follow-ups?leadId=${encodeURIComponent(item.leadId || '')}`)}
                          sx={{ ...pillSx, color: GOLD, border: `1px solid ${GOLD}55` }}>
                          Schedule visit
                        </Box>
                      </Tooltip>
                    ) : (
                      <Box component="button" type="button" onClick={() => confirmDone(item)}
                        sx={{ fontFamily: FONT_BODY, fontSize: 11.5, fontWeight: 600, color: '#2f6b35', bgcolor: 'transparent', border: '1px solid #cfe6d2', borderRadius: 999, px: 1, cursor: 'pointer' }}>
                        Done
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
          );
  }

  // ─── Shared JSX: visits list (today by time, tomorrow collapsed) ──────
  const visitsList = (
    <Box sx={{ overflowY: 'auto', flex: 1 }}>
      {actionError && (
        <Typography sx={{ fontFamily: FONT_BODY, fontSize: 12, color: RED, px: 2, pt: 1 }}>{actionError}</Typography>
      )}
      {visitsToday.length === 0 ? (
        <Typography sx={{ fontFamily: FONT_BODY, fontSize: 12.5, color: MUTED, px: 2, py: 1.25 }}>No site visits today.</Typography>
      ) : (
        visitsToday.map((item, idx) => (
          <React.Fragment key={item.id}>
            {itemRow(item)}
            {idx < visitsToday.length - 1 && <Divider sx={{ mx: 2 }} />}
          </React.Fragment>
        ))
      )}
      {visitsTomorrow.length > 0 && (
        <>
          <Box component="button" type="button" onClick={() => setShowVisitsTomorrow((v) => !v)}
            sx={{ display: 'block', width: '100%', textAlign: 'left', fontFamily: FONT_BODY, fontSize: 12, fontWeight: 700, color: MUTED, bgcolor: '#faf8f4', border: 'none', borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, px: 2, py: 0.75, cursor: 'pointer' }}>
            {showVisitsTomorrow ? '▾' : '▸'} Tomorrow ({visitsTomorrow.length})
          </Box>
          {showVisitsTomorrow && visitsTomorrow.map((item, idx) => (
            <React.Fragment key={item.id}>
              {itemRow(item)}
              {idx < visitsTomorrow.length - 1 && <Divider sx={{ mx: 2 }} />}
            </React.Fragment>
          ))}
        </>
      )}
      {menus}
    </Box>
  );

  function renderMenus() {
    const o = outcomeFor;
    return (
    <>
      <Menu anchorEl={o?.anchor || null} open={!!o} onClose={() => setOutcomeFor(null)}>
        {o && (
          <Box sx={{ px: 2, py: 1, maxWidth: 290 }}>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: 13, fontWeight: 700, color: DARK, mb: 0.5 }}>
              {o.outcome === 'completed' ? `${o.item.name} visited?` : o.outcome === 'no-show' ? `${o.item.name} didn't come?` : `Reschedule ${o.item.name}`}
            </Typography>
            {o.outcome === 'rescheduled' && (
              <Box component="input" type="datetime-local" value={rescheduleAt}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRescheduleAt(e.target.value)}
                sx={{ fontFamily: FONT_BODY, fontSize: 13, width: '100%', p: 0.75, mb: 0.75, border: `1px solid ${BORDER}`, borderRadius: 1, boxSizing: 'border-box' }} />
            )}
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: 11.5, color: MUTED, mb: 1 }}>{OUTCOME_NOTE[o.outcome]}</Typography>
            <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
              <Box component="button" type="button" onClick={() => setOutcomeFor(null)}
                sx={{ ...pillSx, color: MUTED, border: `1px solid ${BORDER}`, py: 0.4 }}>
                Back
              </Box>
              <Box component="button" type="button"
                disabled={o.outcome === 'rescheduled' && !rescheduleAt}
                onClick={() => markVisit(o.item, o.outcome, rescheduleAt ? new Date(rescheduleAt) : undefined)}
                sx={{ ...pillSx, color: '#fff', bgcolor: o.outcome === 'no-show' ? RED : GOLD, border: 'none', py: 0.4, '&:disabled': { opacity: 0.5, cursor: 'default' } }}>
                {o.outcome === 'completed' ? 'Mark visited' : o.outcome === 'no-show' ? "Mark didn't come" : 'Save new time'}
              </Box>
            </Box>
          </Box>
        )}
      </Menu>
      <IgnoreMenu
        anchorEl={ignoreFor?.anchor || null}
        onClose={() => setIgnoreFor(null)}
        suggestPitch={!!ignoreFor?.item.possiblePitch}
        onPick={(key) => ignoreFor && confirmIgnore(ignoreFor.item, key)}
      />
      {confirmDialog}
      <Menu anchorEl={reassignFor?.anchor || null} open={!!reassignFor} onClose={() => setReassignFor(null)}>
        {salesUsers.map((u) => (
          <MenuItem key={u.email} onClick={() => reassignFor && reassign(reassignFor.item, u)} sx={{ fontSize: 13 }}>
            {u.name}
          </MenuItem>
        ))}
      </Menu>
    </>
    );
  }

  // ─── Shared JSX: "Waiting at reception" strip (above the tabs) ─────────
  const receptionStrip = (waiting.length > 0 || withTeam.length > 0) ? (
    <Box sx={{ bgcolor: '#fff8ec', borderBottom: '1px solid #f0c98f', px: 2, py: 1.1, display: 'flex', flexDirection: 'column', gap: 1, flexShrink: 0 }}>
      {waiting.length > 0 && (
        <Typography sx={{ fontFamily: FONT_BODY, fontSize: 11, fontWeight: 700, color: '#a35f00', letterSpacing: 0.5 }}>
          WAITING AT RECEPTION ({waiting.length})
        </Typography>
      )}
      {waiting.map((r) => {
        const mins = minutesSince(r.checkedInAt, nowTick);
        return (
          <Box key={r.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography noWrap sx={{ fontFamily: FONT_BODY, fontSize: 13.5, fontWeight: 700, color: DARK, flex: 1 }}>{r.name}</Typography>
                <Typography sx={{ fontFamily: FONT_BODY, fontSize: 11, fontWeight: 700, color: mins >= 10 ? RED : '#a35f00', flexShrink: 0 }}>{mins} min</Typography>
              </Box>
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: 12, color: '#3d3d3d' }}>
                {receptionSummary(r)}
                {r.ownerEmail || r.ownerName ? ` · ${salesUsers.find((u) => u.email === String(r.ownerEmail || '').toLowerCase())?.name || r.ownerName}` : ''}
              </Typography>
            </Box>
            {(canSeeTeam || (hasDayAccess && r.projectId === currentProjectId)) && <Box component="button" type="button" disabled={takingId === r.id} onClick={() => takeVisitor(r)}
              sx={{ ...pillSx, color: '#fff', bgcolor: GOLD, border: 'none', fontWeight: 700, minHeight: 30, px: 1.5 }}>
              {takingId === r.id ? '…' : "I'll take it"}
            </Box>}
          </Box>
        );
      })}
      {withTeam.map((r) => {
        const mine = String(r.presenterEmail || '').toLowerCase() === myEmailLower;
        return (
          <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, borderTop: waiting.length ? '1px dashed #f0c98f' : 'none', pt: waiting.length ? 0.75 : 0 }}>
            <Typography noWrap sx={{ fontFamily: FONT_BODY, fontSize: 12, fontWeight: 600, color: '#2f6b9a', flex: 1 }}>
              {r.name} · with {mine ? 'you' : r.presenterName || r.presenterEmail} · {minutesSince(r.takenAt, nowTick)} min
            </Typography>
            {mine && (
              <Box component="button" type="button" onClick={() => { if (!readOnly) setFinishFor(r); }}
                sx={{ ...pillSx, color: '#fff', bgcolor: DARK, border: 'none', fontWeight: 700, minHeight: 28, px: 1.5 }}>
                Finish visit
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  ) : null;

  const listBody = (
    <>
      {receptionStrip}
      {tabBar}
      {tab === 'urgent' ? urgentList : tab === 'visits' ? visitsList : conversationList}
      <FinishVisitDialog
        open={!!finishFor}
        leadId={finishFor?.leadId || null}
        visit={finishFor ? reception.find((r) => r.id === finishFor.id) || finishFor : null}
        canRate={canSeeTeam}
        onClose={() => setFinishFor(null)}
        onOpenLead={(id) => { setFinishFor(null); openVisitLog(id); }}
      />
    </>
  );

  // ─── Shared JSX: chat view ─────────────────────────────────────────────
  const chatView = selectedConv ? (
    <Box sx={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
      <ChannelChat
        channel="whatsapp"
        contactId={selectedConv.contactId}
        contactName={selectedConv.contactDisplay}
        leadId={selectedConv.leadId || undefined}
        brokerId={selectedConv.brokerId || undefined}
        projectId={
          selectedConv.leadId
            ? [...myLeads, ...unassignedLeads].find((l) => l.id === selectedConv.leadId)?.projectId || undefined
            : undefined
        }
        height={isMobile ? undefined : CHAT_H}
        showHeader={false}
        hasLead={!!selectedConv.leadId}
        hasBroker={!!selectedConv.brokerId}
        onOpenLead={() => handleOpenLead(selectedConv.contactId)}
        onOpenBroker={() => handleOpenBroker(selectedConv.brokerId)}
      />
    </Box>
  ) : null;

  // ── Collapsed chip ──────────────────────────────────────────────────────
  if (!expanded) {
    const tip = [
      waitingCount ? `${waitingCount} waiting at reception` : '',
      urgentCount ? `${urgentCount} urgent${overdueCount ? ` (${overdueCount} overdue)` : ''}` : '',
      visitCount ? `${visitCount} site visit${visitCount === 1 ? '' : 's'} today` : '',
      totalUnread ? `${totalUnread} unread WhatsApp` : '',
    ].filter(Boolean).join(' · ');
    return (
      <Box sx={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1300 }}>
        <Tooltip title={`${tip} · click to view`} placement="left">
          <Badge
            badgeContent={urgentCount || totalUnread}
            max={99}
            sx={{ '& .MuiBadge-badge': { bgcolor: urgentCount ? '#ff4444' : WA_GREEN, color: '#fff', fontWeight: 800, fontSize: 10, minWidth: 18, height: 18, top: 4, right: 4 } }}
          >
            <Box
              onClick={() => { setTab(urgentCount ? 'urgent' : visitCount ? 'visits' : 'chats'); setExpanded(true); }}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                bgcolor: urgentCount || waitingCount || visitCount ? HEADER_BG : WA_GREEN, color: '#fff',
                borderRadius: 999, px: 1.75, py: 0.875,
                cursor: 'pointer',
                fontFamily: FONT_BODY, fontWeight: 600, fontSize: 13,
                boxShadow: '0 2px 12px rgba(0,0,0,0.25)',
                userSelect: 'none',
                transition: 'all 0.15s ease',
                '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 4px 16px rgba(0,0,0,0.3)' },
              }}
            >
              {urgentCount || waitingCount || visitCount ? <NotificationsActiveOutlinedIcon sx={{ fontSize: 18 }} /> : <WhatsAppIcon sx={{ fontSize: 18 }} />}
              My Today
              {waitingCount > 0 && (
                <Box component="span" sx={{ bgcolor: '#c77700', color: '#fff', borderRadius: 999, px: 0.9, fontSize: 11.5, fontWeight: 700, lineHeight: '20px' }}>
                  {waitingCount} waiting
                </Box>
              )}
              {urgentCount > 0 && totalUnread > 0 && (
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: WA_GREEN, ml: -0.25 }} title={`${totalUnread} unread`} />
              )}
            </Box>
          </Badge>
        </Tooltip>
      </Box>
    );
  }

  // ── Mobile: bottom SwipeableDrawer ─────────────────────────────────────
  if (isMobile) {
    return (
      <SwipeableDrawer
        anchor="bottom"
        open={expanded}
        onClose={handleClose}
        onOpen={() => setExpanded(true)}
        disableSwipeToOpen
        swipeAreaWidth={0}
        PaperProps={{
          sx: {
            bottom: 0,
            marginBottom: 0,
            borderRadius: '16px 16px 0 0',
            height: selectedConv ? '78vh' : 'auto',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
        sx={{
          '& .MuiDrawer-paperAnchorBottom': {
            bottom: '0 !important',
            marginBottom: '0 !important',
          },
        }}
      >
        {/* Drag handle */}
        <Box sx={{ width: 40, height: 4, bgcolor: '#ddd', borderRadius: 2, mx: 'auto', mt: 1.25, mb: 0.5, flexShrink: 0 }} />
        {panelHeader}
        <Divider sx={{ flexShrink: 0 }} />
        {selectedConv ? chatView : listBody}
      </SwipeableDrawer>
    );
  }

  // ── Desktop: fixed bottom-right panel ──────────────────────────────────
  return (
    <Box
      sx={{
        position: 'fixed', bottom: 0, right: 20,
        width: PANEL_W, zIndex: 1300,
        borderRadius: '12px 12px 0 0',
        overflow: 'hidden',
        boxShadow: '0 -4px 24px rgba(0,0,0,0.12)',
        border: `1px solid ${BORDER}`, borderBottom: 'none',
        bgcolor: '#fff',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {panelHeader}
      <Divider />
      {selectedConv ? (
        <Box sx={{ height: CHAT_H, overflow: 'hidden' }}>
          <ChannelChat
            channel="whatsapp"
            contactId={selectedConv.contactId}
            contactName={selectedConv.contactDisplay}
            leadId={selectedConv.leadId || undefined}
            brokerId={selectedConv.brokerId || undefined}
            customerUnitId={selectedConv.customerUnitId || undefined}
            customerUnitNumber={selectedConv.customerUnitNumber || undefined}
            customerProjectName={selectedConv.customerProjectName || undefined}
            customerName={selectedConv.customerName || undefined}
            projectId={
              selectedConv.leadId
                ? [...myLeads, ...unassignedLeads].find((l) => l.id === selectedConv.leadId)?.projectId || undefined
                : undefined
            }
            height={CHAT_H}
            showHeader={false}
            hasLead={!!selectedConv.leadId}
            hasBroker={!!selectedConv.brokerId}
            hasCustomer={!!selectedConv.customerUnitId}
            canOpenCustomerUnit={String(roleId || '').toLowerCase() === 'admin' || String(roleId || '').toLowerCase() === 'super-admin'}
            onOpenLead={() => handleOpenLead(selectedConv.contactId)}
            onOpenBroker={() => handleOpenBroker(selectedConv.brokerId)}
            onOpenCustomerUnit={handleOpenCustomerUnit}
          />
        </Box>
      ) : (
        <Box sx={{ maxHeight: LIST_H + 60, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
          {listBody}
        </Box>
      )}
    </Box>
  );
}
